'use client';

import { AppImageClient } from '@components/appImage';
import { Button } from '@components/button';
import { Dropdown } from '@components/dropdown/ui/Dropdown';
import { FeedbackFormSuccess } from '@components/feedbackFormSuccess';
import { Input } from '@components/input';
import { PhoneInput } from '@components/phoneInput';
import { Textarea } from '@components/textarea';
import { sendAnalyticsEvent } from '@lib/analytics/analytics';
import { ANALYTICS_EVENTS } from '@lib/analytics/analyticsEvents';
import { classNames } from '@lib/classNames/classNames';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import { Contacts } from './Contacts';
import { FeedbackFormFields, FeedbackFormProps } from './types';

import sendLeadFormData from '@/shared/api/sendLeadFormData';
import { LOCALES } from '@/shared/constants/common';
import { Field } from '@/shared/constants/formsFields';
import { getCountriesForPhoneInput } from '@/shared/constants/phoneCodes';
import { useFormSubmitProtection, useUTMParams } from '@/shared/hooks';
import { getAnalyticsClientId } from '@/shared/utils/analyticsClientId';
import { sendSberAdsEvent } from '@/shared/utils/sendSberAdsEvent';

export const FeedbackForm = ({
  formTitle,
  translations,
  fields,
  className,
  locale,
  images,
  ContactsOverride,
  SuccessPopup,
}: FeedbackFormProps) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    resetField,
    formState: { errors },
  } = useForm<FeedbackFormFields>({
    defaultValues: fields.reduce(
      (initialValues, { name, value }) => ({ ...initialValues, [name]: value }),
      {},
    ),
  });
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const { isSubmitting, protectedSubmit } = useFormSubmitProtection();
  const pathname = usePathname();

  const { getUTMParams } = useUTMParams();

  const reset = useCallback(() => {
    Object.keys(getValues()).forEach((field) => resetField(field));
  }, [getValues, resetField]);

  const handleFormSubmit = useCallback(
    async (data: FeedbackFormFields) => {
      const sanitizedData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value),
      );

      sanitizedData.is_bitrix = true;

      if (locale !== LOCALES.ru) {
        sanitizedData.UF_CRM_1762775945 = locale;
      }

      const utmParams = getUTMParams();
      const clientId = await getAnalyticsClientId(locale);

      /**
       * ВНИМАНИЕ: Используем window.location.origin вместо NEXT_PUBLIC_BASE_URL,
       * т.к. деплоим один инстанс с одной переменной, то NEXT_PUBLIC_BASE_URL будет всегда один и тот же
       * далее, роутинг настроен на уровне Next.js, а не через переменные окружения
       * клиентский origin гарантирует, что referer будет соответствовать реальному домену
       */
      const formDataWithUtm = {
        ...(sanitizedData as FeedbackFormFields),
        ...utmParams,
        ...(clientId && { client_id: clientId }),
        submitDate: new Date().toISOString().slice(0, 19),
        referer:
          typeof window !== 'undefined'
            ? window.location.origin
            : process.env.NEXT_PUBLIC_BASE_URL || 'https://dev.2gis.ru',
      };

      try {
        const response = await sendLeadFormData(formDataWithUtm);

        if (response?.meta) {
          toast.error(translations.fieldsFormError, {
            style: {
              background: 'var(--surface-base-secondary)',
              color: 'var(--text-base-primary)',
            },
          });
        } else {
          setIsSuccessPopupVisible(true);
          reset();
          sendAnalyticsEvent(ANALYTICS_EVENTS.SUBMIT_FEEDBACK_FORM);
          sendSberAdsEvent('FormSuccess');
        }
      } catch {
        toast.error(translations.fieldsFormError, {
          style: {
            background: 'var(--surface-base-secondary)',
            color: 'var(--text-base-primary)',
          },
        });
      }
    },
    [locale, getUTMParams, translations.fieldsFormError, reset],
  );

  const onSubmit = useMemo(
    () => protectedSubmit(handleFormSubmit),
    [protectedSubmit, handleFormSubmit],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => reset(), [fields]);

  const getField = ({
    name,
    required,
    className,
    label,
    type,
    options,
  }: Field) => {
    switch (type) {
      case 'textarea':
        return (
          <Textarea
            key={name}
            label={translations[label]}
            className={className}
            watch={watch}
            {...register(name)}
          />
        );
      case 'dropdown':
        return (
          <Dropdown
            key={name}
            className={className}
            label={translations[label]}
            error={errors[name]}
            watch={watch}
            options={options!
              .map(({ label, ...rest }) => ({
                label: translations[label],
                ...rest,
              }))
              .filter(({ label }) => label)}
            {...register(name, {
              required: required && translations[required],
            })}
            onChange={(value: number | string) =>
              setValue(name, value as string)
            }
          />
        );
      case 'phone':
        return (
          <PhoneInput
            key={name}
            className={className}
            label={translations[label]}
            error={errors[name]}
            watch={watch}
            countries={getCountriesForPhoneInput(locale, images.flags)}
            onPhoneChange={(value: string) => setValue('phone', value)}
            {...register(name, {
              required: required && translations[required],
              validate: (value) =>
                !value ||
                value.replace(/\D/g, '').length >= 10 ||
                (translations[required ?? ''] ?? ''),
            })}
          />
        );
      default:
        return (
          <Input
            key={name}
            className={className}
            label={translations[label]}
            error={errors[name]}
            watch={watch}
            {...register(name, {
              required: required && translations[required],
              ...(name === 'email' && {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: translations[required ?? ''] ?? '',
                },
              }),
            })}
          />
        );
    }
  };

  return (
    <>
      <form
        className="rounded-20 bg-surface-base-inverse-primary relative px-12 py-10 max-sm:rounded-[20px] max-sm:px-5 max-sm:py-6"
        onSubmit={handleSubmit(onSubmit)}
        id="form"
      >
        <p
          className={classNames({
            cls: 'max-md:racking-[-0.3px] text-[44px] leading-[52px] font-medium tracking-[-0.4px] max-md:text-4xl max-md:leading-[38px] max-sm:text-[24px] max-sm:leading-[130%]',
            additional: [className],
          })}
        >
          {formTitle ?? translations.formTitle}
        </p>

        {locale === LOCALES.ru && pathname.includes('/print') && (
          <p className="text-text-base-primary mt-5 max-w-[717px] text-[16px] leading-[24px] max-sm:mt-[14px]">
            Если вам нужна распечатка карты города или печать карты большого
            формата — 2ГИС подберёт оптимальное решение.
          </p>
        )}

        <section className="mt-10 mb-2 max-w-[calc(100%-362px)] max-lg:max-w-full max-sm:mt-5">
          {/* 496x562, рендер по ширине 248 */}
          <AppImageClient
            {...images.logo}
            loading="eager"
            alt="logo"
            className="absolute top-[calc(50%-124px)] right-[92px] h-auto w-[248px] max-lg:hidden"
          />

          <div className="mb-5 grid grid-cols-2 gap-5 max-sm:mb-3 max-sm:grid-cols-1 max-sm:gap-3">
            {fields.map((field) => getField(field))}
          </div>
          <div className="mt-3 flex items-center gap-[26px] max-sm:mt-1 max-sm:flex-col max-sm:items-start max-sm:gap-3">
            <Button
              disabled={isSubmitting}
              type="submit"
              className="flex h-14 w-[216px] items-center justify-center whitespace-nowrap max-sm:w-full"
              variant="fourth"
              ariaLabel={translations.submitButtonLabel}
            >
              {translations.submitButtonLabel}
            </Button>
            <div
              className="text-text-base-secondary max-w-[486px] text-[12px] leading-[16px] [&>a]:underline"
              dangerouslySetInnerHTML={{
                __html: translations.submitButtonDisclaimer,
              }}
            />
          </div>
        </section>
      </form>
      {ContactsOverride !== undefined ? (
        ContactsOverride && <ContactsOverride />
      ) : (
        <Contacts translations={translations} locale={locale} />
      )}

      {isSuccessPopupVisible &&
        ((SuccessPopup && (
          <SuccessPopup
            onClose={() => setIsSuccessPopupVisible(false)}
            translations={translations}
          />
        )) || (
          <FeedbackFormSuccess
            onClose={() => setIsSuccessPopupVisible(false)}
            translations={translations}
            locale={locale}
          />
        ))}
    </>
  );
};
